import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { supportAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { Phone, Mail, Save, RefreshCw, Users, ShieldCheck } from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function ContactDetails() {
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [email, setEmail]     = useState('');
  const [phone, setPhone]     = useState('');
  const [saved, setSaved]     = useState({ email: '', phone: '' });
  const [touched, setTouched] = useState({ email: false, phone: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supportAPI.get();
      const e = res.data?.email || '';
      const p = res.data?.phone || '';
      setEmail(e);
      setPhone(p);
      setSaved({ email: e, phone: p });
      setTouched({ email: false, phone: false });
    } catch (err) {
      // 404 just means nothing has been configured yet
      if (err.response?.status !== 404) toast('Failed to load contact details', 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const errors = {
    email: !email.trim()
      ? 'Email is required'
      : (EMAIL_RE.test(email.trim()) ? '' : 'Enter a valid email address'),
    phone: !phone.trim()
      ? 'Phone number is required'
      : (/^\d{10}$/.test(phone.trim()) ? '' : 'Phone must be exactly 10 digits'),
  };
  const isValid = !errors.email && !errors.phone;
  const isDirty = email.trim() !== saved.email || phone.trim() !== saved.phone;

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setTouched({ email: true, phone: true });
    if (!isValid) return;
    setSaving(true);
    try {
      const res = await supportAPI.update(email.trim(), phone.trim());
      const e = res.data?.email ?? email.trim();
      const p = res.data?.phone ?? phone.trim();
      setEmail(e);
      setPhone(p);
      setSaved({ email: e, phone: p });
      toast('Contact details updated', 'success');
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to update contact details', 'error');
    } finally {
      setSaving(false);
    }
  };

  const errStyle = { borderColor: 'var(--red)', boxShadow: '0 0 0 3px rgba(239,68,68,0.12)' };

  return (
    <Layout
      title="Contact Details"
      subtitle="Support contact shown to sellers and customers in their apps"
    >
      {loading ? (
        <div className="page-loader"><div className="spinner" /></div>
      ) : (
        <div style={{ display: 'grid', gap: 20, maxWidth: 720 }}>
          {/* Form */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Admin Contact</span>
              <button type="button" className="btn btn-ghost btn-sm" onClick={load} disabled={saving}>
                <RefreshCw size={13} />
              </button>
            </div>

            <form onSubmit={handleSubmit} noValidate>
              <div className="form-group">
                <label htmlFor="contact-phone">
                  Contact Number <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <input
                  id="contact-phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={phone}
                  placeholder="10-digit mobile number"
                  onChange={e => {
                    setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                    setTouched(t => ({ ...t, phone: true }));
                  }}
                  onBlur={() => setTouched(t => ({ ...t, phone: true }))}
                  style={touched.phone && errors.phone ? errStyle : {}}
                />
                {touched.phone && errors.phone && (
                  <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 5 }}>⚠ {errors.phone}</p>
                )}
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5 }}>
                  Visible to <strong>sellers</strong> and <strong>customers</strong>.
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="contact-email">
                  Email Address <span style={{ color: 'var(--red)' }}>*</span>
                </label>
                <input
                  id="contact-email"
                  type="email"
                  value={email}
                  placeholder="support@yourdomain.com"
                  autoCapitalize="none"
                  autoCorrect="off"
                  onChange={e => {
                    setEmail(e.target.value);
                    setTouched(t => ({ ...t, email: true }));
                  }}
                  onBlur={() => setTouched(t => ({ ...t, email: true }))}
                  style={touched.email && errors.email ? errStyle : {}}
                />
                {touched.email && errors.email && (
                  <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 5 }}>⚠ {errors.email}</p>
                )}
                <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 5 }}>
                  Visible to <strong>sellers only</strong>. Customers will not see this.
                </p>
              </div>

              <div className="modal-footer" style={{ marginTop: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving || !isDirty}>
                  <Save size={14} /> {saving ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>

          {/* Preview */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">Who sees what</span>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <Users size={18} style={{ marginTop: 2, color: 'var(--text-muted)' }} />
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>Seller app</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Phone size={13} /> {saved.phone || '—'}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Mail size={13} /> {saved.email || '—'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <ShieldCheck size={18} style={{ marginTop: 2, color: 'var(--text-muted)' }} />
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 700 }}>Customer app</div>
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Phone size={13} /> {saved.phone || '—'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}